// src/contexts/AuthContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import SessionTimeoutModal from '../components/common/SessionTimeoutModal';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [firmCode, setFirmCode] = useState(() => {
        try {
            return localStorage.getItem('firmCode');
        } catch {
            return null;
        }
    });
    const [permissions, setPermissions] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [username, setUsername] = useState(null);
    const [fullName, setFullName] = useState(null);
    const [userId, setUserId] = useState(null);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Holding / Ana-Alt Firma States
    const [isParentUser, setIsParentUser] = useState(false);
    const [allowedSubCompanies, setAllowedSubCompanies] = useState([]);
    const [activeSubCompanyCode, setActiveSubCompanyCode] = useState(() => {
        try {
            return localStorage.getItem('activeSubCompanyCode') || null;
        } catch { return null; }
    });
    const [activeSubCompanyName, setActiveSubCompanyName] = useState(() => {
        try {
            return localStorage.getItem('activeSubCompanyName') || null;
        } catch { return null; }
    });

    // Madde 6: Sliding Session & Timeout Modal States
    const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const lastActivityRef = useRef(Date.now());
    const lastRefreshTryRef = useRef(0); // Fazla refresh isteğini önlemek için
    const checkIntervalRef = useRef(null);
    const loginTimeRef = useRef(0); // Login anını takip etmek için

    const handleLogout = useCallback(async (shouldRedirect = true, reason = "Bilinmiyor") => {
        try {
            // Madde 6: Logout from server (clears cookie and revokes token)
            await apiClient('Auth/logout-web', { method: 'POST' }).catch(() => { });
        } catch { /* ignore */ }

        localStorage.removeItem('authToken');
        localStorage.removeItem('firmCode');
        localStorage.removeItem('activeSubCompanyCode');
        localStorage.removeItem('activeSubCompanyName');
        setIsAuthenticated(false);
        setFirmCode(null);
        setUserRole(null);
        setUserId(null);
        setUsername(null);
        setFullName(null);
        setTwoFactorEnabled(false);
        setPermissions([]);
        setMustChangePassword(false);
        setIsTimeoutModalOpen(false);
        setIsParentUser(false);
        setAllowedSubCompanies([]);
        setActiveSubCompanyCode(null);
        setActiveSubCompanyName(null);
        if (shouldRedirect) {
            window.location.replace('/');
        }
    }, []);

    const trackActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    const logoutAll = useCallback(async () => {
        try {
            await apiClient('Auth/logout-all', { method: 'POST' });
        } catch { /* ignore */ }
        handleLogout(true);
    }, [handleLogout]);

    // Uygulama ilk yüklendiğinde token'ı kontrol et
    const setAuthInfoFromToken = useCallback((token) => {
        const tokenPayload = parseJwt(token);
        if (tokenPayload) {
            const now = Date.now();
            const exp = tokenPayload.exp * 1000;
            
            // 10 saniye tolerans ile kontrol et
            if (exp + 10000 > now) {
                setIsAuthenticated(true);
                loginTimeRef.current = now;

                const permissionsClaim = tokenPayload.permissions;
                if (permissionsClaim && typeof permissionsClaim === 'string') {
                    try {
                        const parsedPermissions = JSON.parse(permissionsClaim);
                        setPermissions(parsedPermissions);
                    } catch (e) {
                        console.error("Token'daki izinler parse edilemedi:", e);
                        setPermissions([]);
                    }
                } else {
                    setPermissions(Array.isArray(permissionsClaim) ? permissionsClaim : []);
                }

                setUserId(Number(tokenPayload.nameid));
                setUsername(tokenPayload.unique_name);
                setFullName(tokenPayload.fullName || tokenPayload.unique_name);
                setTwoFactorEnabled(tokenPayload.twoFactorEnabled === 'true' || tokenPayload.twoFactorEnabled === true);
                setMustChangePassword(tokenPayload.mustChangePassword === 'true' || tokenPayload.mustChangePassword === true);

                // Holding / Ana-Alt Firma Claims
                const isParent = tokenPayload.isParentUser === 'true' || tokenPayload.isParentUser === true;
                setIsParentUser(isParent);

                if (isParent) {
                    const allowedSubsClaim = tokenPayload.allowedSubCompanies;
                    if (allowedSubsClaim && typeof allowedSubsClaim === 'string') {
                        try {
                            const parsedSubs = JSON.parse(allowedSubsClaim);
                            setAllowedSubCompanies(parsedSubs);
                        } catch (e) {
                            console.error("Token'daki alt firmalar parse edilemedi:", e);
                            setAllowedSubCompanies([]);
                        }
                    } else {
                        setAllowedSubCompanies(Array.isArray(allowedSubsClaim) ? allowedSubsClaim : []);
                    }
                } else {
                    setIsParentUser(false);
                    setAllowedSubCompanies([]);
                    // Eğer parent user değilse active sub-company'leri temizle (güvenlik için)
                    localStorage.removeItem('activeSubCompanyCode');
                    localStorage.removeItem('activeSubCompanyName');
                    setActiveSubCompanyCode(null);
                    setActiveSubCompanyName(null);
                }
            } else {
                handleLogout(false, "Token süresi dolmuş");
            }
        }
    }, [handleLogout]);

    useEffect(() => {
        const onLogoutEvent = (e) => {
            const message = e.detail?.message || "Oturum sonlandırıldı (auth:logout olayı)";
            handleLogout(true, message);
        };
        window.addEventListener('auth:logout', onLogoutEvent);
        return () => window.removeEventListener('auth:logout', onLogoutEvent);
    }, [handleLogout]);

    // Activity Listeners (Sliding Session için)
    useEffect(() => {
        const handleActivity = () => {
            lastActivityRef.current = Date.now();
        };
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('scroll', handleActivity);
        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
        };
    }, []);

    // Session Monitor Loop (1 saniye hassasiyetle)
    useEffect(() => {
        if (!isAuthenticated) {
            if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
            return;
        }

        checkIntervalRef.current = setInterval(async () => {
            const token = localStorage.getItem('authToken');
            if (!token) {
                handleLogout(true, "Token bulunamadı");
                return;
            }

            const payload = parseJwt(token);
            if (!payload) {
                handleLogout(true, "Token parse edilemedi");
                return;
            }

            const exp = payload.exp * 1000;
            const now = Date.now();
            const timeLeft = exp - now;

            const msSinceLastActivity = now - lastActivityRef.current;
            const isActive = msSinceLastActivity < 2000;

            // 1. Proaktif Yenileme (İlk 30 saniye deneme ve her 5 saniyede bir sınırla)
            const msSinceLogin = now - loginTimeRef.current;
            const canTryRefresh = msSinceLogin > 15000 && (now - lastRefreshTryRef.current) > 5000;
            
            // Eğer aktifse ve süre %80'in altına indiyse yenile (120sn için 96sn kalmışsa)
            const totalDuration = (payload.exp - (payload.iat || (payload.exp - 300))) * 1000;
            const isNearExpiry = timeLeft < totalDuration * 0.8;

            if (isActive && isNearExpiry && canTryRefresh && timeLeft > -10000) {
                lastRefreshTryRef.current = now;
                try {
                    const data = await apiClient('Auth/refresh-web', { method: 'POST' });
                    if (data && data.accessToken) {
                        localStorage.setItem('authToken', data.accessToken);
                        setAuthInfoFromToken(data.accessToken);
                    }
                } catch (e) { 
                    // Sessizce yut
                }
            }

            // 2. Uyarı Modalı Kontrolü
            if (!isActive && timeLeft < 30000 && timeLeft > 0) {
                if (!isTimeoutModalOpen) setIsTimeoutModalOpen(true);
                setCountdown(Math.floor(timeLeft / 1000));
            } else if (timeLeft <= -10000) { // 10 saniye tolerans
                setIsTimeoutModalOpen(false);
                clearInterval(checkIntervalRef.current);
                handleLogout(true, "Token süresi doldu (10sn tolerans aşıldı)");
            } else if (isActive && isTimeoutModalOpen) {
                setIsTimeoutModalOpen(false);
            }
        }, 1000);

        return () => clearInterval(checkIntervalRef.current);
    }, [isAuthenticated, isTimeoutModalOpen, handleLogout, setAuthInfoFromToken]);

    const refreshSessionPermissions = useCallback(async () => {
        try {
            const data = await apiClient('Auth/refresh-web', { method: 'POST' });
            if (data && data.accessToken) {
                localStorage.setItem('authToken', data.accessToken);
                setAuthInfoFromToken(data.accessToken);
                return true;
            }
        } catch (_) {
            // Sessizce atla
        }
        return false;
    }, [setAuthInfoFromToken]);

    const extendSession = useCallback(async () => {
        setIsTimeoutModalOpen(false);
        try {
            const data = await apiClient('Auth/refresh-web', { method: 'POST' });
            if (data && data.accessToken) {
                localStorage.setItem('authToken', data.accessToken);
                setAuthInfoFromToken(data.accessToken);
            }
        } catch (e) {
            handleLogout(true);
        }
    }, [handleLogout, setAuthInfoFromToken]);

    useEffect(() => {
        try {
            const token = localStorage.getItem('authToken');
            const storedFirmCode = localStorage.getItem('firmCode');
            if (token && storedFirmCode) {
                if (!firmCode) setFirmCode(storedFirmCode);
                setAuthInfoFromToken(token);
                // Sayfa yenilendiğinde (F5) arka planda güncel yetkileri içeren yeni token al
                apiClient('Auth/refresh-web', { method: 'POST' })
                    .then((data) => {
                        if (data && data.accessToken) {
                            localStorage.setItem('authToken', data.accessToken);
                            setAuthInfoFromToken(data.accessToken);
                        }
                    })
                    .catch(() => { });
            }
        } catch (e) {
            console.error("Oturum kontrol hatası:", e);
            handleLogout(false);
        } finally {
            setLoading(false);
        }
    }, [setAuthInfoFromToken, handleLogout]);


    const login = useCallback(async (firmCodeInput, usernameInput, passwordInput, options = {}) => {
        // setLoading(true); // <--- KÜRESEL LOADING'İ TETİKLEMİYORUZ (Login sayfası kendi loadingini yönetiyor)
        setError(null);
        try {
            localStorage.setItem('firmCode', firmCodeInput);

            const response = await apiClient('Auth/login', {
                method: 'POST',
                body: { firmCode: firmCodeInput, username: usernameInput, password: passwordInput, generateRefreshToken: true, deviceId: 'WEB_BROWSER' },
            });

            if (response && response.token) {
                const { token, mustChangePassword: mcp } = response;
                const apply = () => {
                    localStorage.setItem('authToken', token);
                    setFirmCode(firmCodeInput);
                    setAuthInfoFromToken(token);
                    setMustChangePassword(!!mcp);
                };
                if (options.deferApply) {
                    localStorage.setItem('authToken', token);
                    return { success: true, message: "Giriş başarılı!", mustChangePassword: !!mcp, apply };
                }
                apply();
                return { success: true, message: "Giriş başarılı!", mustChangePassword: !!mcp };
            } else if (response && response.mfaRequired) {
                return {
                    success: true,
                    mfaRequired: true,
                    isSetupRequired: response.isSetupRequired,
                    tempToken: response.tempToken
                };
            } else {
                throw new Error(response.message || 'Giriş başarılı ancak token alınamadı.');
            }
        } catch (error) {
            console.error('Giriş hatası:', error);
            setError(error.message);
            handleLogout(false);
            throw error;
        } finally {
            // setLoading(false);
        }
    }, [setAuthInfoFromToken, handleLogout]);

    const mfaComplete = useCallback(async (tempToken, code, options = {}) => {
        try {
            const response = await apiClient('Auth/mfa/complete', {
                method: 'POST',
                body: { TempToken: tempToken, Code: code, IsMobile: false, GenerateRefreshToken: true, DeviceId: 'WEB_BROWSER' }
            });

            if (response && (response.accessToken || response.token)) {
                const token = response.accessToken || response.token;
                const apply = () => {
                    localStorage.setItem('authToken', token);
                    const storedFirm = localStorage.getItem('firmCode');
                    if (storedFirm) setFirmCode(storedFirm);
                    setAuthInfoFromToken(token);
                    setMustChangePassword(!!response.mustChangePassword);
                };
                if (options.deferApply) {
                    localStorage.setItem('authToken', token);
                    return { success: true, apply };
                }
                apply();
                return { success: true };
            } else {
                throw new Error("MFA doğrulaması sonrası geçersiz yanıt alındı.");
            }
        } catch (error) {
            console.error('MFA hata:', error);
            throw error;
        }
    }, [setAuthInfoFromToken]);

    // <<< GÜNCELLEME: permissions state'ini de context aracılığıyla paylaşıyoruz >>>

    // Helper to check permissions
    const hasPermission = useCallback((permissionKey) => {
        if (!permissions) return false;
        return permissions.includes(permissionKey);
    }, [permissions]);

    const switchSubCompany = useCallback((subCompanyCode, subCompanyName) => {
        if (!subCompanyCode) {
            localStorage.removeItem('activeSubCompanyCode');
            localStorage.removeItem('activeSubCompanyName');
            setActiveSubCompanyCode(null);
            setActiveSubCompanyName(null);
        } else {
            localStorage.setItem('activeSubCompanyCode', subCompanyCode);
            localStorage.setItem('activeSubCompanyName', subCompanyName);
            setActiveSubCompanyCode(subCompanyCode);
            setActiveSubCompanyName(subCompanyName);
        }
        // Sayfayı yenile ki yeni şubenin verileri tüm apilerden baştan çekilsin
        window.location.reload();
    }, []);

    const value = {
        isAuthenticated,
        firmCode,
        userRole,
        username,
        fullName,
        userId,
        twoFactorEnabled,
        loading,
        error,
        permissions,
        hasPermission,
        refreshSessionPermissions,
        mustChangePassword,
        login,
        mfaComplete,
        handleLogout,
        logoutAll,
        trackActivity,
        // Holding / Ana-Alt Firma Değerleri
        isParentUser,
        allowedSubCompanies,
        activeSubCompanyCode,
        activeSubCompanyName,
        switchSubCompany
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <SessionTimeoutModal 
                isOpen={isTimeoutModalOpen}
                countdown={countdown}
                onExtend={extendSession}
                onLogout={() => handleLogout(true)}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthContext');
    }
    return context;
};

function parseJwt(token) {
    try {
        if (!token) return null;
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("Token okunamadı:", e);
        return null;
    }
}